import { FC, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { Alert } from 'react-daisyui';
import Routes from '@/router/Routes.ts';
import BrandCardSideContent from '@/components/ui/BrandCardSideContent/BrandCardSideContent.tsx';
import LoadingButton from '@/components/functional/LoadingButton/LoadingButton.tsx';
import SelectionRadio from '@/components/functional/SelectionRadio/SelectionRadio.tsx';
import StepFormInput from '@/components/functional/StepFormInput/StepFormInput.tsx';
import StepFormTextarea from '@/components/functional/StepFormTextarea/StepFormTextarea.tsx';
import StepFormActions from '@/components/shared/StepFormActions/StepFormActions.tsx';
import StepFormHint from '@/components/ui/StepFormHint/StepFormHint.tsx';
import StepFormSubtitle from '@/components/ui/StepFormSubtitle/StepFormSubtitle.tsx';
import StepFormTitle from '@/components/ui/StepFormTitle/StepFormTitle.tsx';
import Stepper from '@/components/shared/Stepper/Stepper.tsx';
import { useStepperContext } from '@/components/shared/Stepper/StepperContext.tsx';
import useFrameworkRoutePreview from '@/api/useFrameworkRoutePreview.ts';
import useFrameworkRoute from '@/api/useFrameworkRoute.ts';
import type { FrameworkRoutePreviewResponse } from '$/requests/FrameworkRoute.ts';
import {
  buildFrameworkRouteCommitRequest,
  buildFrameworkRoutePreviewRequest,
  shouldEnterAmbiguityState,
  type ClarityDiagnosticCapture,
} from '@/pages/clarityDiagnostic/buildFrameworkRoutePayload.ts';
import {
  INTERPRETATION_CONFIDENCE_KEYS,
  PRIMARY_CONSTRAINT_KEYS,
  PRIMARY_ISSUE_TYPE_KEYS,
} from '@/pages/clarityDiagnostic/signalConfirmationOptions.ts';
import { formatGovScore } from '@/utils/sessionTableDisplay.ts';
import { ClipboardDocumentListIcon, QuestionMarkCircleIcon } from '@heroicons/react/24/outline';

const steps = [
  'interpretation-reveal',
  'signal-confirmation',
  'ambiguity-state',
  'diagnostic-summary',
  'reflection-commitment',
] as const;

const REFLECTION_MAX_LENGTH = 280;

function mergePreviewResult(
  base: Partial<ClarityDiagnosticCapture>,
  result: FrameworkRoutePreviewResponse,
): Partial<ClarityDiagnosticCapture> {
  return {
    ...base,
    is_ambiguous: result.is_ambiguous,
    ambiguity_question: result.ambiguity_question,
    diagnostic_summary: result.diagnostic_summary,
    assumption_record: result.assumption_record,
    risk_clarification: result.risk_clarification,
    stakeholder_impact: result.stakeholder_impact,
    approval_readiness_score: result.approval_readiness_score,
  };
}

const ClarityDiagnosticLayer: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const decisionId =
    (location.state as { decisionId?: string } | null)?.decisionId ?? undefined;

  if (!decisionId) {
    return (
      <div className="flex w-full justify-center p-8">
        <Alert status="error">{t('clarityDiagnosticLayerPage.errors.noDecision')}</Alert>
      </div>
    );
  }

  return (
    <div className="flex w-full justify-center pb-8">
      <Stepper.Wrapper
        steps={steps}
        initialStep="interpretation-reveal"
        initialContextData={{ decision_id: decisionId }}
      >
        <Stepper.Step<typeof steps> step="interpretation-reveal">
          <StepInterpretationReveal />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="signal-confirmation">
          <StepSignalConfirmation />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="ambiguity-state">
          <StepAmbiguityState />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="diagnostic-summary">
          <StepDiagnosticSummary />
        </Stepper.Step>
        <Stepper.Step<typeof steps> step="reflection-commitment">
          <StepReflectionCommitment />
        </Stepper.Step>
      </Stepper.Wrapper>
    </div>
  );
};

export default ClarityDiagnosticLayer;

function StepInterpretationReveal() {
  const { t } = useTranslation();
  const { nextStep } = useStepperContext<typeof steps, ClarityDiagnosticCapture>();

  return (
    <BrandCardSideContent centerContent>
      <div className="flex flex-col flex-1 gap-6 w-full">
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('clarityDiagnosticLayerPage.interpretation.title')}</StepFormTitle>
          <StepFormSubtitle>{t('clarityDiagnosticLayerPage.interpretation.subtitle')}</StepFormSubtitle>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5 border border-base-300 rounded-2xl p-4">
            <div className="flex flex-row items-center gap-2">
              <ClipboardDocumentListIcon className="w-[22px] h-[22px] text-black flex-shrink-0" />
              <p className="font-md font-bold text-black/80">
                {t('clarityDiagnosticLayerPage.interpretation.commandLabel')}
              </p>
            </div>
            <p className="font-dm text-sm text-black/60 leading-relaxed">
              {t('clarityDiagnosticLayerPage.interpretation.commandBody')}
            </p>
          </div>
          <div className="flex flex-col gap-1.5 border border-base-300 rounded-2xl p-4">
            <div className="flex flex-row items-center gap-2">
              <QuestionMarkCircleIcon className="w-[22px] h-[22px] text-black flex-shrink-0" />
              <p className="font-md font-bold text-black/80">
                {t('clarityDiagnosticLayerPage.interpretation.meansLabel')}
              </p>
            </div>
            <p className="font-dm text-sm text-black/60 leading-relaxed">
              {t('clarityDiagnosticLayerPage.interpretation.meansBody')}
            </p>
          </div>
        </div>
        <LoadingButton loading={false} color="primary" className="w-full mt-auto" type="button" onClick={nextStep}>
          {t('button.continue')}
        </LoadingButton>
      </div>
    </BrandCardSideContent>
  );
}

type SignalConfirmationFields = {
  primary_issue_type: string;
  primary_constraint: string;
  constraint_other: string;
  interpretation_confidence: string;
};

function StepSignalConfirmation() {
  const { t } = useTranslation();
  const { nextStep, previousStep, goToStep, setContextData, contextData } =
    useStepperContext<typeof steps, ClarityDiagnosticCapture>();
  const d = contextData;
  const { mutateAsync: runFrameworkRoutePreview, isPending } = useFrameworkRoutePreview();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const issueTypeOptions = useMemo(
    () =>
      PRIMARY_ISSUE_TYPE_KEYS.map((key) => ({
        key,
        label: t(`clarityDiagnosticLayerPage.signal.primaryIssueType.${key}`),
      })),
    [t],
  );
  const constraintOptions = useMemo(
    () =>
      PRIMARY_CONSTRAINT_KEYS.map((key) => ({
        key,
        label: t(`clarityDiagnosticLayerPage.signal.primaryConstraint.${key}`),
      })),
    [t],
  );
  const confidenceOptions = useMemo(
    () =>
      INTERPRETATION_CONFIDENCE_KEYS.map((key) => ({
        key,
        label: t(`clarityDiagnosticLayerPage.signal.interpretationConfidence.${key}`),
      })),
    [t],
  );

  const { register, handleSubmit, watch, setValue } = useForm<SignalConfirmationFields>({
    defaultValues: {
      primary_issue_type: d?.primary_issue_type ?? '',
      primary_constraint: d?.primary_constraint ?? '',
      constraint_other: d?.constraint_other ?? '',
      interpretation_confidence: d?.interpretation_confidence ?? '',
    },
  });

  const primaryIssueType = watch('primary_issue_type');
  const primaryConstraint = watch('primary_constraint');
  const constraintOther = watch('constraint_other');
  const interpretationConfidence = watch('interpretation_confidence');

  const onSubmit = (data: SignalConfirmationFields) => {
    void (async () => {
      setSubmitError(null);
      const merged: Partial<ClarityDiagnosticCapture> = {
        ...(d ?? {}),
        primary_issue_type: data.primary_issue_type,
        primary_constraint: data.primary_constraint,
        constraint_other: data.constraint_other.trim(),
        interpretation_confidence: data.interpretation_confidence,
        ambiguity_clarifications: [],
      };

      try {
        const body = buildFrameworkRoutePreviewRequest(merged);
        const result = await runFrameworkRoutePreview(body);
        const ambiguityQuestion =
          result.ambiguity_question?.trim() ||
          t('clarityDiagnosticLayerPage.ambiguity.fallbackQuestion');

        setContextData({
          ...mergePreviewResult(merged, result),
          ambiguity_question: ambiguityQuestion,
        });

        if (shouldEnterAmbiguityState(data.interpretation_confidence, result.is_ambiguous)) {
          nextStep();
        } else {
          goToStep('diagnostic-summary');
        }
      } catch (error) {
        console.error(error);
        setSubmitError(
          error instanceof Error ? error.message : t('clarityDiagnosticLayerPage.errors.routeFailed'),
        );
      }
    })();
  };

  const constraintOtherValid =
    primaryConstraint !== 'other' || constraintOther.trim().length > 0;
  const canContinue =
    !!primaryIssueType &&
    !!primaryConstraint &&
    !!interpretationConfidence &&
    constraintOtherValid;

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('clarityDiagnosticLayerPage.signal.title')}</StepFormTitle>
          <StepFormSubtitle>{t('clarityDiagnosticLayerPage.signal.subtitle')}</StepFormSubtitle>
        </div>
        {submitError && <Alert status="error">{submitError}</Alert>}
        <div className="flex flex-col gap-2">
          <StepFormHint>{t('clarityDiagnosticLayerPage.signal.primaryIssueTypeLabel')}</StepFormHint>
          <div className="flex flex-col gap-2">
            {issueTypeOptions.map(({ key, label }) => (
              <SelectionRadio
                key={key}
                label={label}
                selected={primaryIssueType === key}
                onClick={() => setValue('primary_issue_type', key, { shouldValidate: true })}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-2">
          <StepFormHint>{t('clarityDiagnosticLayerPage.signal.primaryConstraintLabel')}</StepFormHint>
          <div className="flex flex-col gap-2">
            {constraintOptions.map(({ key, label }) => (
              <SelectionRadio
                key={key}
                label={label}
                selected={primaryConstraint === key}
                onClick={() => setValue('primary_constraint', key, { shouldValidate: true })}
              />
            ))}
          </div>
          {primaryConstraint === 'other' && (
            <StepFormInput
              placeholder={t('clarityDiagnosticLayerPage.signal.constraintOtherPlaceholder')}
              {...register('constraint_other', { required: true })}
            />
          )}
        </div>
        <div className="flex flex-col gap-2">
          <StepFormHint>{t('clarityDiagnosticLayerPage.signal.interpretationConfidenceLabel')}</StepFormHint>
          <div className="flex flex-col gap-2">
            {confidenceOptions.map(({ key, label }) => (
              <SelectionRadio
                key={key}
                label={label}
                selected={interpretationConfidence === key}
                onClick={() => setValue('interpretation_confidence', key, { shouldValidate: true })}
              />
            ))}
          </div>
        </div>
        <StepFormActions
          onBack={previousStep}
          continueDisabled={!canContinue || isPending}
          continueLoading={isPending}
          continueLabel={isPending ? t('clarityDiagnosticLayerPage.signal.analyzing') : undefined}
        />
      </form>
    </BrandCardSideContent>
  );
}

type AmbiguityStateFields = {
  clarification: string;
};

function StepAmbiguityState() {
  const { t } = useTranslation();
  const { previousStep, goToStep, setContextData, contextData } =
    useStepperContext<typeof steps, ClarityDiagnosticCapture>();
  const d = contextData;
  const { mutateAsync: runFrameworkRoutePreview, isPending } = useFrameworkRoutePreview();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<AmbiguityStateFields>({
    mode: 'onChange',
    defaultValues: { clarification: '' },
  });

  const ambiguityQuestion = d?.ambiguity_question?.trim();

  const onSubmit = (data: AmbiguityStateFields) => {
    void (async () => {
      setSubmitError(null);
      const clarification = data.clarification.trim();
      const clarifications = [...(d?.ambiguity_clarifications ?? []), clarification];
      const merged: Partial<ClarityDiagnosticCapture> = {
        ...(d ?? {}),
        ambiguity_clarifications: clarifications,
      };

      try {
        const body = buildFrameworkRoutePreviewRequest(merged);
        const result = await runFrameworkRoutePreview(body);
        setContextData(mergePreviewResult(merged, result));
        reset({ clarification: '' });

        if (!result.is_ambiguous) {
          goToStep('diagnostic-summary');
        }
      } catch (error) {
        console.error(error);
        setSubmitError(
          error instanceof Error ? error.message : t('clarityDiagnosticLayerPage.errors.routeFailed'),
        );
      }
    })();
  };

  if (!ambiguityQuestion) {
    return (
      <BrandCardSideContent>
        <div className="flex flex-col flex-1 gap-6 w-full">
          <Alert status="warning">{t('clarityDiagnosticLayerPage.summary.missingData')}</Alert>
          <LoadingButton
            loading={false}
            color="primary"
            className="w-full"
            type="button"
            onClick={() => goToStep('signal-confirmation')}
          >
            {t('button.back')}
          </LoadingButton>
        </div>
      </BrandCardSideContent>
    );
  }

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('clarityDiagnosticLayerPage.ambiguity.title')}</StepFormTitle>
          <StepFormSubtitle>{t('clarityDiagnosticLayerPage.ambiguity.subtitle')}</StepFormSubtitle>
        </div>
        {submitError && <Alert status="error">{submitError}</Alert>}
        <div className="flex flex-col gap-2">
          <StepFormHint>{ambiguityQuestion}</StepFormHint>
          <StepFormTextarea
            placeholder={t('clarityDiagnosticLayerPage.ambiguity.typeHere')}
            {...register('clarification', { required: true })}
          />
          <StepFormHint>{t('clarityDiagnosticLayerPage.ambiguity.inputNote')}</StepFormHint>
        </div>
        <StepFormActions
          onBack={previousStep}
          continueDisabled={!isValid || isPending}
          continueLoading={isPending}
        />
      </form>
    </BrandCardSideContent>
  );
}

function DiagnosticSummaryBlock({
  label,
  value,
}: {
  label: string;
  value: string;
}) {
  return (
    <div className="flex flex-col gap-1">
      <p className="font-dm text-xs font-semibold uppercase tracking-wide text-black/50">{label}</p>
      <p className="font-dm text-sm text-black/70 leading-relaxed">{value}</p>
    </div>
  );
}

function StepDiagnosticSummary() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { nextStep, previousStep, goToStep, contextData } =
    useStepperContext<typeof steps, ClarityDiagnosticCapture>();
  const d = contextData;

  const hasDiagnostic = !!d?.diagnostic_summary?.trim() && d?.is_ambiguous === false;

  return (
    <BrandCardSideContent>
      <div className="flex flex-col flex-1 gap-6 w-full">
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('clarityDiagnosticLayerPage.summary.title')}</StepFormTitle>
          <StepFormSubtitle>{t('clarityDiagnosticLayerPage.summary.subtitle')}</StepFormSubtitle>
        </div>

        {!hasDiagnostic ? (
          <Alert status="warning">{t('clarityDiagnosticLayerPage.summary.missingData')}</Alert>
        ) : (
          <div className="rounded-2xl bg-[#F4F9FF] p-5 flex flex-col gap-4 font-dm text-sm text-black/70 leading-relaxed">
            <p>{d?.diagnostic_summary}</p>
            {d?.approval_readiness_score != null && (
              <div
                className="tooltip cursor-default before:max-w-[260px] before:text-left before:text-xs before:font-dm"
                data-tip="Reflects the clarity, constraint specificity, and risk profile of this case. A higher score indicates a well-structured decision ready for framework routing."
              >
                <p className="font-semibold text-black/80 underline decoration-dotted underline-offset-2">
                  {t('clarityDiagnosticLayerPage.summary.approvalReadiness', {
                    score: formatGovScore(d.approval_readiness_score),
                  })}
                </p>
              </div>
            )}
            {d?.assumption_record && (
              <DiagnosticSummaryBlock
                label={t('clarityDiagnosticLayerPage.summary.assumptions')}
                value={d.assumption_record}
              />
            )}
            {d?.risk_clarification && (
              <DiagnosticSummaryBlock
                label={t('clarityDiagnosticLayerPage.summary.risks')}
                value={d.risk_clarification}
              />
            )}
            {d?.stakeholder_impact && (
              <DiagnosticSummaryBlock
                label={t('clarityDiagnosticLayerPage.summary.stakeholders')}
                value={d.stakeholder_impact}
              />
            )}
          </div>
        )}

        <StepFormActions
          onBack={previousStep}
          submitType="button"
          onContinue={nextStep}
          continueDisabled={!hasDiagnostic}
          continueLabel={t('clarityDiagnosticLayerPage.summary.confirmDiagnostic')}
          secondaryAction={
            <>
              <button
                type="button"
                className="w-full py-2 px-[22px] h-[42px] bg-white border border-[rgba(0,0,0,0.12)] rounded-xl font-dm font-bold text-sm text-[#0F1B2A] tracking-[0.46px] transition-colors hover:bg-black/5"
                onClick={() => goToStep('signal-confirmation')}
              >
                {t('clarityDiagnosticLayerPage.summary.editCase')}
              </button>
              <button
                type="button"
                className="w-full py-2 font-dm font-bold text-sm text-[#991B1B] text-center"
                onClick={() =>
                  d?.decision_id &&
                  navigate(Routes.RECOMMENDATION_ENGINE, {
                    state: { decisionId: d.decision_id, resumeStep: 'fiduciary-escalation' },
                  })
                }
              >
                {t('clarityDiagnosticLayerPage.summary.escalateAssessment')}
              </button>
            </>
          }
        />
      </div>
    </BrandCardSideContent>
  );
}

type ReflectionCommitmentFields = {
  confirmation_sentence: string;
};

function StepReflectionCommitment() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { previousStep, contextData } = useStepperContext<typeof steps, ClarityDiagnosticCapture>();
  const d = contextData;
  const { mutateAsync: commitFrameworkRoute, isPending: isCommitting } = useFrameworkRoute();
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    formState: { isValid },
  } = useForm<ReflectionCommitmentFields>({
    mode: 'onChange',
    defaultValues: {
      confirmation_sentence: d?.confirmation_sentence ?? '',
    },
  });

  const confirmationSentence = watch('confirmation_sentence');

  const onSubmit = (data: ReflectionCommitmentFields) => {
    void (async () => {
      setSubmitError(null);
      const merged: Partial<ClarityDiagnosticCapture> = {
        ...(d ?? {}),
        confirmation_sentence: data.confirmation_sentence.trim(),
      };

      try {
        const body = buildFrameworkRouteCommitRequest(merged);
        await commitFrameworkRoute(body);
        navigate(Routes.RECOMMENDATION_ENGINE, { state: { decisionId: d?.decision_id } });
      } catch (error) {
        console.error(error);
        setSubmitError(
          error instanceof Error ? error.message : t('clarityDiagnosticLayerPage.errors.routeFailed'),
        );
      }
    })();
  };

  const withinMaxLength = confirmationSentence.length <= REFLECTION_MAX_LENGTH;
  const canContinue = isValid && withinMaxLength && confirmationSentence.trim().length > 0;

  return (
    <BrandCardSideContent>
      <form className="flex flex-col flex-1 gap-6 w-full" onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col gap-2">
          <StepFormTitle>{t('clarityDiagnosticLayerPage.reflection.title')}</StepFormTitle>
          <StepFormSubtitle>{t('clarityDiagnosticLayerPage.reflection.subtitle')}</StepFormSubtitle>
        </div>
        {submitError && <Alert status="error">{submitError}</Alert>}
        <div className="flex flex-col gap-2">
          <StepFormHint>{t('clarityDiagnosticLayerPage.reflection.instruction')}</StepFormHint>
          <StepFormTextarea
            placeholder={t('clarityDiagnosticLayerPage.reflection.typeHere')}
            maxLength={REFLECTION_MAX_LENGTH}
            {...register('confirmation_sentence', { required: true })}
          />
          <StepFormHint>{t('clarityDiagnosticLayerPage.reflection.hint')}</StepFormHint>
        </div>
        <StepFormActions
          onBack={previousStep}
          continueDisabled={!canContinue || isCommitting}
          continueLoading={isCommitting}
          continueLabel={t('clarityDiagnosticLayerPage.reflection.continueRecommendation')}
        />
      </form>
    </BrandCardSideContent>
  );
}